import type {
  CockpitData,
  CockpitMetric,
} from "../types/executive-cockpit.types";
// Presentation guard only: never turn Application counts into Interview facts.
export function enforceCockpitDisplayContract<T>(input: T): T {
  const meta = (input as { meta?: CockpitData["meta"] })?.meta;
  const visitKeys = new Set(["attended", "passed", "ytd"]);
  function visit(value: unknown, key = ""): unknown {
    if (Array.isArray(value)) return value.map((v) => visit(v));
    if (!value || typeof value !== "object") return value;
    const object = value as Record<string, unknown>;
    if ("grain" in object && "quality" in object && "value" in object) {
      const m = { ...object } as CockpitMetric;
      const expected = visitKeys.has(key)
        ? "interview"
        : ["resources", "new_resources", "active_workers"].includes(key)
          ? "worker"
          : key === "applications"
            ? "application"
            : undefined;
      m.display_unit =
        expected === "worker"
          ? "people"
          : expected === "interview" ||
              (key === "applications" &&
                meta?.mode === "cohort" &&
                meta?.cohort_basis === "schedule")
            ? "visits"
            : "records";
      m.demo =
        meta?.data_mode === "synthetic_ui_fixture" ||
        m.source === "synthetic_ui_fixture";
      if (expected && m.grain !== expected) {
        m.value = null;
        m.quality = "unavailable";
        m.contract_issue = "grain_mismatch";
      }
      if (
        ["active_workers", "ytd"].includes(key) &&
        m.value == null &&
        m.unknown_count !== 0 &&
        !m.contract_issue
      ) {
        m.contract_issue = "coverage_incomplete";
      }
      return m;
    }
    return Object.fromEntries(
      Object.entries(object).map(([k, v]) => [k, visit(v, k)]),
    );
  }
  return visit(input) as T;
}

export function canDisplayCockpitRate(
  n?: CockpitMetric,
  d?: CockpitMetric,
  event = false,
): boolean {
  return (
    !event &&
    !n?.contract_issue &&
    !d?.contract_issue &&
    n?.quality === "complete" &&
    d?.quality === "complete" &&
    n.unknown_count === 0 &&
    d.unknown_count === 0 &&
    n.value != null &&
    d.value != null &&
    Number.isFinite(n.value) &&
    Number.isFinite(d.value) &&
    d.value > 0 &&
    n.value >= 0 &&
    n.value <= d.value &&
    (n.display_unit || n.unit) === (d.display_unit || d.unit) &&
    n.conversion_basis === "linked_single_interview_per_application" &&
    d.conversion_basis === n.conversion_basis &&
    n.grain === "interview" &&
    ["application", "interview"].includes(d.grain)
  );
}
